import { ModalStackIdleContext, TryShowModalContext } from 'contexts/ModalProvider'
import { useAppSelector } from 'hooks/reduxHooks'
import { useLabRouteRequestContext } from 'hooks/useLabRouteRequestContext'
import { popupForPage, popupsFromUis } from 'lib/labV2/popups'
import { SNACKBAR_AFTER_POPUP_MS, snackbarForPage, snackbarsFromUis } from 'lib/labV2/snackbars'
import { renderInlineMarkdown } from 'lib/string/markdownUtils'
import Label from 'LuxKit/Label'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { isLabV2ModalSuppressed, recordLabV2ModalSeen } from 'storage/labV2Modal'
import { isLabV2SnackbarSuppressed } from 'storage/labV2Snackbar'
import { presentLabV2Snackbar } from '../LabSnackbar/useLabV2Snackbar'

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

export default function useLabV2Modal({ enabled }: { enabled: boolean }) {
  const tryShowModal = useContext(TryShowModalContext)
  const isModalStackIdle = useContext(ModalStackIdleContext)
  const { pathname, currentRegionCode } = useLabRouteRequestContext()
  const uis = useAppSelector(state => state.system.features.labV2?.uis)
  const popups = useMemo(() => popupsFromUis(uis), [uis])
  const snackbarGenerationRef = useRef(0)
  const snackbarTimeoutRef = useRef(0)

  // Modal-stack changes must not cancel delivery; leaving the page must.
  useEffect(() => () => {
    snackbarGenerationRef.current += 1
    window.clearTimeout(snackbarTimeoutRef.current)
  }, [pathname, currentRegionCode, enabled])

  const popup = useMemo(() => {
    if (!enabled || !isModalStackIdle) {
      return undefined
    }
    const candidate = popupForPage(popups, pathname, currentRegionCode)
    if (!candidate || isLabV2ModalSuppressed(candidate)) {
      return undefined
    }
    return candidate
  }, [enabled, isModalStackIdle, popups, pathname, currentRegionCode])

  useEffect(() => {
    if (!isModalStackIdle || !popup || isLabV2ModalSuppressed(popup)) {
      return
    }

    const content = popup.content
    const snackbarGeneration = snackbarGenerationRef.current
    const snackbar = snackbarForPage(snackbarsFromUis(uis), pathname, currentRegionCode)
    const closing = tryShowModal(() => {
      recordLabV2ModalSeen(popup)
      return (
        <Modal
          title=""
          isHeaderOverlay
          image={content.imageId ? { id: content.imageId, title: content.title } : undefined}
          imageParams={content.imageId ? { mobileAspectRatio: '2:1', tabletAspectRatio: '2:1', desktopAspectRatio: '2:1' } : undefined}
          primaryActionText={content.primaryCta?.label}
          primaryActionTo={content.primaryCta?.path}
          secondaryActionText={content.secondaryCta?.label}
          secondaryActionTo={content.secondaryCta?.path}
        >
          <ContentStack>
            {content.tag ? <Label kind="primary" variant={content.tag.variant}>{content.tag.text}</Label> : null}
            <Heading variant="heading3" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(content.title) }} />
            {content.description ? (
              <BodyText variant="large" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(content.description) }} />
            ) : null}
          </ContentStack>
        </Modal>
      )
    })
    closing?.then(() => {
      if (snackbarGeneration !== snackbarGenerationRef.current || !snackbar || isLabV2SnackbarSuppressed(snackbar)) {
        return
      }
      snackbarTimeoutRef.current = window.setTimeout(() => {
        presentLabV2Snackbar(snackbar)
      }, SNACKBAR_AFTER_POPUP_MS)
    })
  }, [currentRegionCode, isModalStackIdle, pathname, popup, tryShowModal, uis])
}
